import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  Box,
  Typography,
  SwipeableDrawer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard,
  PrecisionManufacturing,
  Description,
  Schedule,
  Assessment,
  NotificationsActive,
  People,
  Person,
  Tune,
  Assignment,
} from '@mui/icons-material';
import type { NavigationConfig, NavigationItem, SidebarState } from '../shared';

export const SIDEBAR_EXPANDED_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

interface NavGroup {
  title: string;
  items: NavigationConfig[];
}

const NAVIGATION_GROUPS: NavGroup[] = [
  {
    title: 'Home',
    items: [
      { id: 'dashboard', label: 'Home', icon: Dashboard, path: '/' },
      { id: 'assignments', label: 'My Tasks', icon: Assignment, path: '/assignments' },
      { id: 'results', label: 'Results', icon: Assessment, path: '/results' },
      { id: 'alerts', label: 'Alerts', icon: NotificationsActive, path: '/alerts' },
    ],
  },
  {
    title: 'Admin',
    items: [
      { id: 'machines', label: 'Machines', icon: PrecisionManufacturing, path: '/machines' },
      { id: 'templates', label: 'Templates', icon: Description, path: '/templates' },
      { id: 'schedules', label: 'Schedules', icon: Schedule, path: '/schedules' },
      { id: 'config', label: 'Configuration', icon: Tune, path: '/config' },
    ],
  },
  {
    title: 'Settings',
    items: [
      { id: 'users', label: 'Users', icon: People, path: '/users' },
      { id: 'profile', label: 'Profile', icon: Person, path: '/profile' },
    ],
  },
];

// Flat list for active item detection
const ALL_NAVIGATION_ITEMS: NavigationConfig[] = NAVIGATION_GROUPS.flatMap((g) => g.items);

export interface CollapsibleSidebarProps {
  alertCount?: number;
  sidebarState?: SidebarState;
  onToggle?: (collapsed: boolean) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  onMobileOpen?: () => void;
  collapsed?: boolean;
}

function getActiveItem(pathname: string): NavigationItem | null {
  for (const item of ALL_NAVIGATION_ITEMS) {
    if (pathname === item.path || pathname.startsWith(item.path + '/')) {
      return item.id;
    }
  }
  return null;
}

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  alertCount = 0,
  sidebarState,
  onToggle,
  mobileOpen = false,
  onMobileClose,
  onMobileOpen,
  collapsed: collapsedProp,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [internalCollapsed] = React.useState(false);

  const collapsed = collapsedProp !== undefined ? collapsedProp : sidebarState ? sidebarState.collapsed : internalCollapsed;
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
  const activeItem = getActiveItem(location.pathname);


  const handleNavigation = (item: NavigationConfig) => {
    navigate(item.path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const sidebarContent = (
    <Box sx={{ px: collapsed && !isMobile ? 1 : 2, pt: 1 }}>
      {NAVIGATION_GROUPS.map((group) => (
        <Box key={group.title} sx={{ mb: 1.5 }}>
          {/* Group header */}
          {(!collapsed || isMobile) && (
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.6875rem',
                letterSpacing: '0.5px',
              }}
            >
              {group.title}
            </Typography>
          )}

          <List component="nav" disablePadding>
            {group.items.map((item) => {
              const isActive = activeItem === item.id;
              const Icon = item.icon;
              const showBadge = item.id === 'alerts' && alertCount > 0;

              const iconElement = showBadge ? (
                <Badge badgeContent={alertCount} color="error">
                  <Icon fontSize="small" />
                </Badge>
              ) : (
                <Icon fontSize="small" />
              );

              const button = (
                <ListItemButton
                  key={item.id}
                  selected={isActive}
                  onClick={() => handleNavigation(item)}
                  aria-current={isActive ? 'page' : undefined}
                  sx={{
                    minHeight: 44,
                    justifyContent: collapsed && !isMobile ? 'center' : 'initial',
                    px: 2,
                    borderRadius: '12px',
                    mb: 0.5,
                    transition: 'all .2s ease-in-out',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed && !isMobile ? 0 : 2,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    {iconElement}
                  </ListItemIcon>
                  {(!collapsed || isMobile) && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400 }}
                    />
                  )}
                </ListItemButton>
              );

              if (collapsed && !isMobile) {
                return (
                  <Tooltip key={item.id} title={item.label} placement="right">
                    {button}
                  </Tooltip>
                );
              }

              return <React.Fragment key={item.id}>{button}</React.Fragment>;
            })}
          </List>
        </Box>
      ))}
    </Box>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose || (() => {})}
        onOpen={onMobileOpen || (() => {})}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_EXPANDED_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'background.default',
            borderRight: 'none',
          },
        }}
      >
        {/* Logo in mobile drawer */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, minHeight: 64 }}>
          <Typography variant="h5" fontWeight={700} color="primary.main" noWrap>
            TrueOne
          </Typography>
        </Box>
        {sidebarContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, width: { xs: 'auto', md: width } }}
      aria-label="Primary navigation"
    >
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            bgcolor: 'background.default',
            borderRight: 'none',
            marginTop: '88px',
            height: 'calc(100vh - 88px)',
            zIndex: theme.zIndex.appBar - 1,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen + 200,
            }),
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </Box>
  );
};

export default CollapsibleSidebar;
